import moment, { Moment } from 'moment';
import { Vue, Component } from 'vue-property-decorator';
import { ALL_TRANSLATIONS } from '../translations/all';
import {
  LocationEntity,
  EntranceEntity,
  TranslationLanguage,
  LocationVisualSettings,
  ScanningQueryStringParameters,
  CurrentScanningLocation,
} from '../../../types';
import { parseQueryParameters } from '@/helpers';

@Component
export default class GlobalMixIn extends Vue {
  // Data Properties

  public fullScreenLoader = null as any;

  // Methods

  public moment = moment;

  public showLoader = () => {
    this.fullScreenLoader = (this as any).$loading.show({
      container: null,
      loader: 'spinner',
      color: this.primaryColor,
      width: 100,
      height: 100,
    });
  };

  public hideLoader = () => {
    if (this.fullScreenLoader !== null) {
      this.fullScreenLoader.hide();
    }
  };

  public formatMomentWithLocale = (momentObject: Moment, formatString: string) => {
    return momentObject.locale(this.$root.$i18n.locale).format(formatString);
  };

  public getAllLocations = (): Promise<void> => {
    return this.$store.dispatch('appData/GET_ALL_LOCATIONS', {
      apiKey: this.apiKey,
      customerId: this.customerId,
    });
  };

  public getLocation = (locationId: number): Promise<void> => {
    return this.$store.dispatch('appData/GET_LOCATION', {
      apiKey: this.apiKey,
      customerId: this.customerId,
      locationId,
    });
  };

  public getCustomerData = (): Promise<void> => {
    return this.$store.dispatch('appData/GET_CUSTOMER_APP_DATA');
  };

  public startLoading = () => {
    this.$store.commit('appData/SET_LOADING', true);

    this.showLoader();
  };

  public stopLoading = () => {
    this.$store.commit('appData/SET_LOADING', false);

    this.hideLoader();
  };

  public setLocale = (locale: string) => {
    if (this.$root.$i18n.availableLocales.includes(locale)) {
      this.$root.$i18n.locale = locale;
    }
  };

  public getLocationName = (location: LocationEntity): string => {
    return this.$root.$i18n.locale === 'en'
      ? location.name
      : location.nameTranslations && location.nameTranslations[this.$root.$i18n.locale]
      ? location.nameTranslations[this.$root.$i18n.locale]
      : location.name;
  };

  public setInitialValues = (): void => {
    const location: LocationEntity | null = (this as any).currentLocation;

    if (location) {
      if (location.translations) {
        (this as any).setTranslations(location.translations, this.$root.$i18n.locale);
      }

      if (location.visualSettings) {
        (this as any).setVisualSettings(location.visualSettings);
      }
    } else {
      const locationFallbackVisualSettings = this.locations.find(
        (locationInner) => locationInner.visualSettings && locationInner.visualSettings !== null,
      );

      if (locationFallbackVisualSettings) {
        (this as any).setVisualSettings(locationFallbackVisualSettings.visualSettings);
      }
      const locationFallbackTranslations = this.locations.find((locationInner) => locationInner.translations && locationInner.translations !== null);

      if (locationFallbackTranslations) {
        (this as any).setVisualSettings(locationFallbackTranslations.visualSettings);
      }
    }
  };

  public setTranslations = (translations: TranslationLanguage[] | null, languageOverride?: string) => {
    const defaultTranslations = JSON.parse(JSON.stringify(ALL_TRANSLATIONS));

    if (translations === null) {
      Object.keys(defaultTranslations).forEach((langCode: string) => {
        this.$root.$i18n.setLocaleMessage(langCode, (defaultTranslations as any)[langCode]);
      });

      this.setLocale(languageOverride || 'en');

      return;
    }

    const defaultLanguageObject = translations.find((translation) => translation.default);

    const defaultLanguage = defaultLanguageObject ? defaultLanguageObject.langCode : 'en';

    translations.forEach((translation) => {
      const combinedTranslations = (defaultTranslations as any)[translation.langCode]
        ? Object.assign(translation.phrases, (defaultTranslations as any)[translation.langCode])
        : translation.phrases;

      this.$root.$i18n.setLocaleMessage(translation.langCode, combinedTranslations);
    });

    this.setLocale(languageOverride || defaultLanguage);
  };

  public setVisualSettings = (visualSettings: LocationVisualSettings | null): void => {
    if (visualSettings !== null) {
      const newQueryParameters = Object.assign(this.queryParameters, visualSettings);

      this.$store.commit('appData/SET_QUERY_PARAMETERS', newQueryParameters);
    }
  };

  public updateQueryParameters() {
    const existingQueryParameters = this.$store.getters['appData/QUERY_PARAMETERS'];
    const newQueryParameters = this.$route.query;

    const queryParameters =
      Object.keys(newQueryParameters).length === 0 ? parseQueryParameters(existingQueryParameters) : parseQueryParameters(newQueryParameters);

    (this as any).setLocale(queryParameters.lang);

    this.$store.commit('appData/SET_QUERY_PARAMETERS', queryParameters);
  }

  // Computed Properties

  get locations(): LocationEntity[] {
    return this.$store.getters['appData/LOCATIONS'];
  }

  get entrances(): EntranceEntity[] {
    return this.$store.getters['appData/ENTRANCES'];
  }

  get queryParameters(): ScanningQueryStringParameters {
    return this.$store.getters['appData/QUERY_PARAMETERS'];
  }

  get globalLoading(): boolean {
    return this.$store.getters['appData/LOADING'];
  }

  get currentScanningLocation(): CurrentScanningLocation {
    return this.$store.getters['appData/CURRENT_SCANNING_LOCATION'];
  }

  get currentLocationId(): number | null {
    return this.currentScanningLocation.id;
  }

  get currentLocation(): LocationEntity | null {
    return this.locations.find((location) => location.id === this.currentLocationId) || null;
  }

  get currentLocationSecurityScanningCode(): string | null {
    return this.currentScanningLocation.securityScanningCode;
  }

  get currentLocationLastDataPull(): number | null {
    return this.currentScanningLocation.lastDataPull;
  }

  get currentLocationEntranceId(): number | null {
    return this.currentScanningLocation.entranceId;
  }

  // Query Parameters

  get apiKey(): string | null {
    return this.queryParameters.apiKey;
  }

  get customerId(): number | null {
    return this.queryParameters.customerId;
  }

  get locationId(): number | null {
    return this.queryParameters.locationId;
  }

  get securityScanningCode(): string | null {
    return this.queryParameters.securityScanningCode;
  }

  get externalUserId(): string | null {
    return this.queryParameters.externalUserId;
  }

  get lang(): string {
    return this.queryParameters.lang;
  }

  get cssURL(): string | null {
    return this.queryParameters.cssURL;
  }

  get primaryColor(): string | null {
    return this.queryParameters.primaryColor;
  }

  get primaryFontColor(): string | null {
    return this.queryParameters.primaryFontColor;
  }

  get primaryButtonFontColor(): string | null {
    return this.queryParameters.primaryButtonFontColor;
  }

  get secondColor(): string | null {
    return this.queryParameters.secondColor;
  }

  get secondFontColor(): string | null {
    return this.queryParameters.secondFontColor;
  }

  get secondButtonFontColor(): string | null {
    return this.queryParameters.secondButtonFontColor;
  }

  get headerBackgroundColor(): string | null {
    return this.queryParameters.headerBackgroundColor;
  }

  get approveColor(): string | null {
    return this.queryParameters.approveColor;
  }

  get denyColor(): string | null {
    return this.queryParameters.denyColor;
  }
}
